import { computedFrom } from "aurelia-binding";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import * as Enumerable from "linq";
import * as moment from "moment";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { RatingsSummaryModel, VesselDetailModel, VesselHelper } from "../../../../../../models/vessels/vessel.models";
import { RiskScoreHelper, VetClientModel, VetStatusEnum, VetTaskClientModel } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { CompSafetyScore } from "@rightship-tech/common-ui/components/comp-safety-score/comp-safety-score";
import { ChildComponent } from "../../../child-component";
import { VetRunRiskLevelsEnum } from "../../../ports-and-terminals/models/ports-and-terminals.models";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { CustomerSubscriptionService } from "../../../../../../services/modules/customer-subscription-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { CustomerVesselService } from "../../../../../../services/vessels/customer-vessels.service";

export abstract class BaseVetOverview extends ChildComponent<VetClientModel>{

    public isAutoScreen: boolean;
    public showBerthInVetRequest: boolean;
    public isShowMoreSectionDetails: boolean;
    public isShowMoreSectionStatus: boolean;
    public vessel: VesselDetailModel;
    public compSafetyScore: CompSafetyScore;
    public decisionComments: string;
    public hasScreening: boolean = true; //TODO
    private showAIExperiment: boolean;
    protected isCustomer: boolean;

    constructor(notificationsService: NotificationClientService, i18n: I18N, private vetClient: VettingService, 
        protected dialogClientService: DialogClientService, protected userClaimsService: UserClaimsService, protected signalClientService: SignalClientService,
        protected manageVesselService: ManageVesselService, protected router: Router,
        protected userClientService: UserClientService,
        protected customerSubscriptionService: CustomerSubscriptionService,
        protected customerVesselsService: CustomerVesselService, protected configService: ConfigService) {
        super(notificationsService, i18n);
        this.showBerthInVetRequest = this.userClaimsService.hasPermission(Claims.ShowBerthInVetRequest);
        this.showAIExperiment = this.userClaimsService.hasPermission(Claims.ShowAIExperiment);
        this.isCustomer = !this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    @computedFrom("item", "item.vetStatusId")
    get notProcessing() {
        return this.item && this.item.vetStatusId != VetStatusEnum.Processing;
    }

    @computedFrom("vetRiskScores")
    get vetRiskScoresEnumerable() { return Enumerable.from(this.vetRiskScores).where(x => x.name == 'All'); }

    @computedFrom("vetRiskScores")
    get countLowFail() { return this.vetRiskScoresEnumerable.sum(x => x.countLowFail); }

    @computedFrom("vetRiskScores")
    get countMediumFail() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumFail); }

    @computedFrom("vetRiskScores")
    get countHighFail() { return this.vetRiskScoresEnumerable.sum(x => x.countHighFail); }

    @computedFrom("vetRiskScores")
    get countOraFail() { return this.vetRiskScoresEnumerable.sum(x => x.countOraFail); }

    @computedFrom("vetRiskScores")
    get countLowPass() { return this.vetRiskScoresEnumerable.sum(x => x.countLowPass); }

    @computedFrom("vetRiskScores")
    get countMediumPass() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumPass); }

    @computedFrom("vetRiskScores")
    get countHighPass() { return this.vetRiskScoresEnumerable.sum(x => x.countHighPass); }

    @computedFrom("vetRiskScores")
    get countOraPass() { return this.vetRiskScoresEnumerable.sum(x => x.countOraPass); }

    @computedFrom("vetRiskScores")
    get countAllLow() { return this.vetRiskScoresEnumerable.sum(x => x.countLowFail + x.countLowPass); }

    @computedFrom("vetRiskScores")
    get countAllMedium() { return this.vetRiskScoresEnumerable.sum(x => x.countMediumFail + x.countMediumPass); }

    @computedFrom("vetRiskScores")
    get countAllHigh() { return this.vetRiskScoresEnumerable.sum(x => x.countHighFail + x.countHighPass); }

    @computedFrom("vetRiskScores")
    get countAllOra() { return this.vetRiskScoresEnumerable.sum(x => x.countOraFail + x.countOraPass); }


    async attached() {
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        this.userClientService.subscribeOnUserUpdate(this.onPermissionsChanged);

        this.refreshRating({ vesselIds: [this.item.vesselId] });
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshRating);
        this.userClientService.unsubscribeOnUserUpdate(this.onPermissionsChanged)
    }

    protected onPermissionsChanged: (userResponse) => Promise<void> = () => { return new Promise<void>((resolve) => { resolve(); }) };

    isCategoryCustomer(cat: string) {
        return Enumerable.from(this.item.vetTasks).where(x => x.category == cat).any(x => x.categoryIsCustomer);
    }


    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    @computedFrom("item")
    get vetRiskScores() {
        const vetRiskScores = this.item.vetRiskScores;
        if (vetRiskScores.filter(x => x.name == 'Critical').length == 0) {
            vetRiskScores.push({
                name: "Critical",
                riskLevelId: VetRunRiskLevelsEnum.Low,
                riskLevel: "Low",
                countHighFail: 0,
                countHighPass: 0,
                countLowFail: 0,
                countLowPass: 0,
                countMediumFail: 0,
                countMediumPass: 0,
                countOraFail: 0,
                countOraPass: 0,
                sequence: 2,
                riskScore: null,
                title: "Critical Score",
                riskCategoryId: -1
            });
        }
        if (vetRiskScores.filter(x => x.name == 'Soft').length == 0) {
            vetRiskScores.push({
                name: "Soft",
                riskLevelId: VetRunRiskLevelsEnum.Low,
                riskLevel: "Low",
                countHighFail: 0,
                countHighPass: 0,
                countLowFail: 0,
                countLowPass: 0,
                countMediumFail: 0,
                countMediumPass: 0,
                countOraFail: 0,
                countOraPass: 0,
                sequence: 3,
                riskScore: null,
                title: "Soft Score",
                riskCategoryId: -1
            });
        }

        const scores = this.item ? Enumerable.from(vetRiskScores).orderBy(x => x.sequence).toArray() : [];

        for (const score of scores) {
            score.riskScore = RiskScoreHelper.calculateRiskScore({
                countAllHigh: score.countHighFail + score.countHighPass,
                countAllLow: score.countLowFail + score.countLowPass,
                countAllMedium: score.countMediumFail + score.countMediumPass,
                countHighFail: score.countHighFail,
                countLowFail: score.countLowFail,
                countMediumFail: score.countMediumFail,
                countOraFail: score.countOraFail
            });
        }

        return scores;
    }

    ratingModel: RatingsSummaryModel;
    private refreshRating = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.item.vesselId).length >= 1) {
            try {
                const response = await this.manageVesselService.getRatingsModel(this.item.vesselId);
                this.ratingModel = response;
            } catch (e) {
                console.error("Failed to refresh rating: ", e);
            }
        }
    }

    @computedFrom("item", "item.vetTasks", "item.vetTasks.length", "task.vetSuperVetTaskOutcomeId")
    get pendingRules() {
        return this.item.vetTasks.filter(task => !task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0).length;
    }


    @computedFrom("item", "item.portCountryOfLoadSanctioned", "item.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.item.portCountryOfLoadSanctioned || this.item.portCountryOfDischargeSanctioned;
    }

    @computedFrom("item", "item.portCountryOfDischargeUnknown", "item.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.item.portCountryOfDischargeUnknown || this.item.portCountryOfLoadUnknown;
    }

    @computedFrom("item", "item.built")
    get built() {
        const date = moment(this.item.built);
        const yearAge = VesselHelper.getAgeFromDate(date);
        return yearAge;
    }

    @computedFrom("vessel.internationalMaritimeOrganizationNumber")
    get aiExpirimentLink() {

        if (this.vessel && this.vessel.internationalMaritimeOrganizationNumber && this.showAIExperiment) {
            return this.configService.config.urls.redMarbleUrl + '/VesselInfo?imo=' + this.vessel.internationalMaritimeOrganizationNumber
        }

        return null;
    }

    showMoreSectionDetails() {
        this.isShowMoreSectionDetails = !this.isShowMoreSectionDetails;
    }

    showMoreSectionStatus() {
        this.isShowMoreSectionStatus = !this.isShowMoreSectionStatus;
    }

    async openAdditionalInformationDialog(task: VetTaskClientModel) {
        if (task.hasListItems && !task.hasRsListItems) {
            const listItems = await this.vetClient.getListItems(task.vetTaskId);
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                listItems: listItems,
                task: task,
                vet: this.item
            }, true, false, false, "right");
        }
        else if (task.hasCap) {
            const cap = await this.vetClient.getCapResponse(this.item.vetId, task.vetTaskId);
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                cap: cap,
                task: task,
                vet: this.item
            }, true, false, false, "right");
        }
        else if (task.canViewDisplayResultsInOverview) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                task: task,
                vet: this.item
            }, true, false, false, "right");
        }
    }
}